import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import moment from "moment";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
} from "@mui/material";
import {
  Add,
  ContentCopy,
  Delete,
  ExpandMore,
  QrCode2,
  Settings,
  Payments,
  Message as MessageIcon,
  CheckCircle,
} from "@mui/icons-material";
import Autocomplete from "@material-ui/lab/Autocomplete";

import MainContainer from "../../components/MainContainer";
import ForbiddenPage from "../../components/ForbiddenPage";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const STATUS = {
  pending: { label: "Pendente", color: "#f9a825", bg: "#fff8e1" },
  paid: { label: "Pago", color: "#2e7d32", bg: "#e8f5e9" },
  overdue: { label: "Atrasado", color: "#c62828", bg: "#ffebee" },
  canceled: { label: "Cancelado", color: "#757575", bg: "#f5f5f5" },
};

const REMINDER_LABELS = [
  { key: "reminderD5", label: "5 dias antes" },
  { key: "reminderD3", label: "3 dias antes" },
  { key: "reminderD1", label: "1 dia antes" },
  { key: "reminderD0", label: "No dia do vencimento" },
  { key: "reminderOverdue", label: "1 dia após (atrasado)" },
];

const Charges = () => {
  const { user } = useContext(AuthContext);

  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  // Dialog nova cobrança
  const [newOpen, setNewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contactOptions, setContactOptions] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchContact, setSearchContact] = useState("");
  const [form, setForm] = useState({ description: "", value: "", dueDate: moment().add(5, "days").format("YYYY-MM-DD"), installments: "1" });

  // Dialog QR
  const [qrCharge, setQrCharge] = useState(null);

  // Config
  const [mpToken, setMpToken] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);

  // Mensagens personalizáveis dos lembretes
  const [msgs, setMsgs] = useState({ msgD5: "", msgD3: "", msgD1: "", msgD0: "", msgOverdue: "", msgPaid: "" });
  const [savingMsgs, setSavingMsgs] = useState(false);

  const loadCharges = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/charges");
      setCharges(data.charges || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/charges/settings");
      setSettings(data);
      setMsgs({
        msgD5: data.msgD5 || "",
        msgD3: data.msgD3 || "",
        msgD1: data.msgD1 || "",
        msgD0: data.msgD0 || "",
        msgOverdue: data.msgOverdue || "",
        msgPaid: data.msgPaid || "",
      });
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveMsgs = async () => {
    setSavingMsgs(true);
    try {
      const { data } = await api.put("/charges/settings", msgs);
      setSettings(data);
      toast.success("Mensagens salvas!");
    } catch (err) {
      toastError(err);
    }
    setSavingMsgs(false);
  };

  useEffect(() => {
    if (user.profile === "admin") {
      loadCharges();
      loadSettings();
    }
    // eslint-disable-next-line
  }, []);

  // Buscar contatos no autocomplete
  useEffect(() => {
    if (!newOpen) return;
    const delay = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", { params: { searchParam: searchContact } });
        setContactOptions(data.contacts || []);
      } catch (err) {
        // silencioso
      }
    }, 400);
    return () => clearTimeout(delay);
  }, [searchContact, newOpen]);

  const handleCreate = async () => {
    if (!selectedContact) return toast.warn("Selecione um contato.");
    if (!form.description || !form.value || !form.dueDate) return toast.warn("Preencha todos os campos.");
    setSaving(true);
    try {
      const parcelas = Math.max(1, parseInt(form.installments, 10) || 1);
      const { data } = await api.post("/charges", {
        contactId: selectedContact.id,
        description: form.description,
        value: Number(form.value),
        dueDate: form.dueDate,
        installments: parcelas,
      });
      const criadas = data?.count || 1;
      toast.success(criadas > 1 ? `${criadas} parcelas criadas com PIX!` : "Cobrança criada e PIX gerado!");
      setNewOpen(false);
      setSelectedContact(null);
      setForm({ description: "", value: "", dueDate: moment().add(5, "days").format("YYYY-MM-DD"), installments: "1" });
      loadCharges();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancelar esta cobrança?")) return;
    try {
      await api.delete(`/charges/${id}`);
      toast.success("Cobrança cancelada.");
      loadCharges();
    } catch (err) {
      toastError(err);
    }
  };

  const handleMarkPaid = async (id) => {
    if (!window.confirm("Confirmar o recebimento desta cobrança? Ela entra como receita no fluxo de caixa.")) return;
    try {
      await api.post(`/charges/${id}/pay`);
      toast.success("Cobrança marcada como paga!");
      loadCharges();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveToken = async () => {
    if (!mpToken.trim()) return toast.warn("Cole o token do Mercado Pago.");
    setSavingSettings(true);
    try {
      const { data } = await api.put("/charges/settings", { mpAccessToken: mpToken.trim() });
      setSettings(data);
      setMpToken("");
      toast.success("Token do Mercado Pago salvo!");
    } catch (err) {
      toastError(err);
    }
    setSavingSettings(false);
  };

  const toggleReminder = async (key) => {
    try {
      const { data } = await api.put("/charges/settings", { [key]: !settings[key] });
      setSettings(data);
    } catch (err) {
      toastError(err);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Código PIX copiado!");
  };

  if (user.profile !== "admin") {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>

        {/* Cabeçalho */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box sx={{ width: 44, height: 44, borderRadius: 2.5, background: alpha("#2e7d32", 0.15), display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Payments sx={{ color: "#2e7d32" }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>Cobranças</Typography>
              <Typography variant="body2" color="text.secondary">Gere cobranças PIX e envie lembretes automáticos no WhatsApp</Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setNewOpen(true)}
            disabled={!settings?.mpConfigured}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
          >
            Nova Cobrança
          </Button>
        </Stack>

        {/* Aviso se MP não configurado */}
        {settings && !settings.mpConfigured && (
          <Card elevation={0} sx={{ mb: 3, borderRadius: 3, border: "1.5px solid", borderColor: "#ffcc80", background: alpha("#f9a825", 0.08) }}>
            <CardContent>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#e65100" }}>
                ⚠️ Configure o token do Mercado Pago abaixo para começar a gerar cobranças.
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Configuração */}
        <Accordion elevation={0} sx={{ mb: 3, borderRadius: 3, border: "1.5px solid", borderColor: "divider", "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Settings fontSize="small" color="action" />
              <Typography sx={{ fontWeight: 600 }}>Configuração</Typography>
              {settings?.mpConfigured && <Chip size="small" label="Mercado Pago conectado" sx={{ background: alpha("#2e7d32", 0.15), color: "#2e7d32", fontWeight: 600 }} />}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Token do Mercado Pago</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Access Token da conta MP desta empresa. O dinheiro cai direto na conta dela.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  placeholder={settings?.mpConfigured ? "•••••••• (já configurado — cole para substituir)" : "APP_USR-..."}
                  value={mpToken}
                  onChange={(e) => setMpToken(e.target.value)}
                />
                <Button
                  variant="outlined"
                  onClick={handleSaveToken}
                  disabled={savingSettings}
                  sx={{ mt: 1.5, borderRadius: 2, textTransform: "none" }}
                >
                  {savingSettings ? <CircularProgress size={20} /> : "Salvar token"}
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Lembretes automáticos</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                  Quando enviar lembrete no WhatsApp para o cliente.
                </Typography>
                {settings && REMINDER_LABELS.map((r) => (
                  <FormControlLabel
                    key={r.key}
                    control={<Switch checked={!!settings[r.key]} onChange={() => toggleReminder(r.key)} size="small" />}
                    label={<Typography variant="body2">{r.label}</Typography>}
                    sx={{ display: "flex", ml: 0 }}
                  />
                ))}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Mensagens personalizáveis */}
        <Accordion elevation={0} sx={{ mb: 3, borderRadius: 3, border: "1.5px solid", borderColor: "divider", "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <MessageIcon fontSize="small" color="action" />
              <Typography sx={{ fontWeight: 600 }}>Mensagens dos lembretes</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              Personalize o texto de cada mensagem. Use os marcadores: <b>{"{nome}"}</b> (nome do cliente), <b>{"{valor}"}</b> (valor da cobrança), <b>{"{vencimento}"}</b> (data de vencimento) e <b>{"{link}"}</b> (link de pagamento PIX).
            </Typography>
            <Grid container spacing={2}>
              {[
                { key: "msgD5", label: "5 dias antes do vencimento" },
                { key: "msgD3", label: "3 dias antes do vencimento" },
                { key: "msgD1", label: "1 dia antes do vencimento" },
                { key: "msgD0", label: "No dia do vencimento" },
                { key: "msgOverdue", label: "Cobrança atrasada" },
                { key: "msgPaid", label: "Confirmação de pagamento" },
              ].map((m) => (
                <Grid item xs={12} md={6} key={m.key}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>{m.label}</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    value={msgs[m.key]}
                    onChange={(e) => setMsgs({ ...msgs, [m.key]: e.target.value })}
                  />
                </Grid>
              ))}
            </Grid>
            <Button
              variant="contained"
              onClick={handleSaveMsgs}
              disabled={savingMsgs}
              sx={{ mt: 2, borderRadius: 2, textTransform: "none", fontWeight: 600 }}
            >
              {savingMsgs ? <CircularProgress size={20} /> : "Salvar mensagens"}
            </Button>
          </AccordionDetails>
        </Accordion>

        {/* Lista de cobranças */}
        <Card elevation={0} sx={{ borderRadius: 3, border: "1.5px solid", borderColor: "divider", overflow: "hidden" }}>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Cliente</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Valor</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Vencimento</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {charges.map((c) => {
                  const st = STATUS[c.status] || STATUS.pending;
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.contact?.name || "-"}</TableCell>
                      <TableCell>{c.description}</TableCell>
                      <TableCell align="right">{Number(c.value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}</TableCell>
                      <TableCell align="center">{moment(c.dueDate).format("DD/MM/YYYY")}</TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={st.label} sx={{ background: st.bg, color: st.color, fontWeight: 600 }} />
                      </TableCell>
                      <TableCell align="center">
                        {c.pixQrCodeBase64 && (
                          <Tooltip title="Ver QR Code / PIX">
                            <IconButton size="small" onClick={() => setQrCharge(c)}><QrCode2 fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {c.status !== "paid" && c.status !== "canceled" && (
                          <Tooltip title="Marcar como pago">
                            <IconButton size="small" onClick={() => handleMarkPaid(c.id)}><CheckCircle fontSize="small" sx={{ color: "#2e7d32" }} /></IconButton>
                          </Tooltip>
                        )}
                        {c.status !== "paid" && c.status !== "canceled" && (
                          <Tooltip title="Cancelar">
                            <IconButton size="small" onClick={() => handleCancel(c.id)}><Delete fontSize="small" color="error" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {!loading && charges.length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>Nenhuma cobrança ainda.</TableCell></TableRow>
                )}
                {loading && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Card>
      </Box>

      {/* Dialog nova cobrança */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 700 }}>Nova Cobrança</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Autocomplete
              options={contactOptions}
              getOptionLabel={(o) => (o.number ? `${o.name} - ${o.number}` : o.name)}
              value={selectedContact}
              onChange={(e, v) => setSelectedContact(v)}
              onInputChange={(e, v) => setSearchContact(v)}
              renderInput={(params) => (
                <TextField {...params} label="Contato" variant="outlined" size="small" fullWidth />
              )}
            />
            <TextField
              label="Descrição"
              size="small"
              fullWidth
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
            <TextField
              label="Valor"
              size="small"
              fullWidth
              type="number"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            />
            <TextField
              label={Number(form.installments) > 1 ? "Vencimento da 1ª parcela" : "Vencimento"}
              size="small"
              fullWidth
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Parcelas"
              size="small"
              fullWidth
              type="number"
              value={form.installments}
              onChange={(e) => setForm({ ...form, installments: e.target.value })}
              inputProps={{ min: 1, max: 60 }}
              helperText={
                Number(form.installments) > 1 && Number(form.value) > 0
                  ? `${Number(form.installments)}x de ${(Number(form.value) / Number(form.installments)).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} — 1 cobrança por mês`
                  : "1 = cobrança única. Acima disso, gera 1 cobrança por mês (valor total dividido)."
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setNewOpen(false)} sx={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving} sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
            {saving ? <CircularProgress size={20} /> : "Gerar PIX"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog QR Code */}
      <Dialog open={!!qrCharge} onClose={() => setQrCharge(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>PIX — {qrCharge?.contact?.name}</DialogTitle>
        <DialogContent>
          {qrCharge && (
            <Stack spacing={2} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#2e7d32" }}>
                {Number(qrCharge.value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
              </Typography>
              {qrCharge.pixQrCodeBase64 && (
                <img
                  src={`data:image/png;base64,${qrCharge.pixQrCodeBase64}`}
                  alt="QR Code PIX"
                  style={{ width: 220, height: 220 }}
                />
              )}
              <Divider flexItem />
              <Typography variant="caption" color="text.secondary">PIX Copia e Cola:</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
                <TextField size="small" fullWidth value={qrCharge.pixCopyPaste || ""} InputProps={{ readOnly: true }} />
                <IconButton onClick={() => copyToClipboard(qrCharge.pixCopyPaste)}><ContentCopy fontSize="small" /></IconButton>
              </Box>
              {qrCharge.paymentLink && (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => window.open(qrCharge.paymentLink, "_blank")}
                  sx={{ textTransform: "none", borderRadius: 2 }}
                >
                  Abrir link de pagamento
                </Button>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrCharge(null)} sx={{ textTransform: "none" }}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Charges;
